import React, { useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import NewsList from "./NewsList";
import { useTranslations } from "src/contexts/TranslationContext";
import RobotsNoIndex from "src/components/seo/RobotsNoIndex";

interface NewsProps {
  lang?: string;
  type?: string;
  subtype?: string;
}

const News: React.FC<NewsProps> = ({ lang, type: propType = 'trending', subtype: propSubtype }) => {
  // Track the current mounted instance to prevent stale effects
  const isMounted = useRef(true);
  
  // Track the last rendered type, subtype and locale to avoid redundant updates
  const lastTypeRef = useRef<string>('');
  const lastSubtypeRef = useRef<string | undefined>(undefined);
  const lastLocaleRef = useRef<string>('');
  
  // Read the type and subtype parameters from URL query
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const queryType = queryParams.get('type');
  const querySubtype = queryParams.get('subtype');
  
  // Use the translations context to get the current language
  const { language, translations: t } = useTranslations();
  
  // Memoize the finalType and finalSubtype to prevent unnecessary re-renders
  const finalType = useMemo(() => {
    const newType = queryType || propType;
    console.log(`Type calculation: queryType=${queryType}, propType=${propType}, final=${newType}`);
    return newType;
  }, [queryType, propType]);
  
  const finalSubtype = useMemo(() => {
    const newSubtype = querySubtype || propSubtype;
    console.log(`Subtype calculation: querySubtype=${querySubtype}, propSubtype=${propSubtype}, final=${newSubtype}`);
    return newSubtype;
  }, [querySubtype, propSubtype]);
  
  // Use the language from context, fallback to prop if provided
  const finalLocale = lang || language;
  
  // Only log once when type, subtype or language actually changes
  useEffect(() => {
    if (finalType !== lastTypeRef.current || 
        finalSubtype !== lastSubtypeRef.current ||
        finalLocale !== lastLocaleRef.current) {
      console.log(`News component actual change - type: ${finalType} (was: ${lastTypeRef.current}), subtype: ${finalSubtype} (was: ${lastSubtypeRef.current}), language: ${finalLocale} (was: ${lastLocaleRef.current})`);
      lastTypeRef.current = finalType;
      lastSubtypeRef.current = finalSubtype;
      lastLocaleRef.current = finalLocale;
    }
  }, [finalType, finalSubtype, finalLocale]);
  
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);
  
  const navigate = useNavigate();
  const { getUrlLanguagePrefix } = useTranslations();

  // Get translated type name
  const getTypeTranslation = (type: string) => {
    if (type === 'trending') return t?.navigations?.header?.trending || 'Top News';
    if (type === 'entertainment') return t?.navigations?.header?.entertainment || 'Entertainment';
    if (type === 'sports') return t?.navigations?.header?.sports || 'Sports';
    return type.charAt(0).toUpperCase() + type.slice(1); // Fallback to capitalized type
  };

  // Get translated subtype name
  const getSubtypeTranslation = (subtype: string) => {
    if (t?.navigations?.header?.[subtype]) {
      return t.navigations.header[subtype];
    }
    return subtype.charAt(0).toUpperCase() + subtype.slice(1); // Fallback to capitalized subtype
  };
  
  // Helper function to handle type clicks - use same pattern as in NewsList
  const handleTypeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const path = `/${finalType}`;
    const fullPath = `${getUrlLanguagePrefix(language)}${path}`;
    console.log(`Navigating to type: ${finalType}, fullPath: ${fullPath}`);
    navigate(fullPath);
  };
  
  // Helper function to handle subtype clicks - use same pattern as in NewsList 
  const handleSubtypeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const path = `/${finalType}/${finalSubtype}`;
    const fullPath = `${getUrlLanguagePrefix(language)}${path}`;
    console.log(`Navigating to subtype: ${finalSubtype}, fullPath: ${fullPath}`);
    navigate(fullPath);
  };
  
  return (
    <div>
      {/* Add robots noindex meta tag when a subtype is present */}
      {finalSubtype && <RobotsNoIndex />}
      
      {/* Title section to display the current category and subcategory */}
      <div className="bg-white border-b border-gray-200 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-2xl font-bold capitalize text-gray-800 flex items-center space-x-3">
            {finalSubtype ? (
              <>
                <span 
                  onClick={handleTypeClick}
                  className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                >
                  {getTypeTranslation(finalType)}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
                <span className="text-gray-400">/</span> 
                <span 
                  onClick={handleSubtypeClick}
                  className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
                >
                  {getSubtypeTranslation(finalSubtype)}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </>
            ) : (
              <span
                onClick={handleTypeClick}
                className="cursor-pointer text-blue-600 hover:text-blue-800 font-medium hover:underline inline-flex items-center"
              >
                {getTypeTranslation(finalType)}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            )}
          </h1>
        </div>
      </div>
      {/* <NewsEmbeded /> */}
      <NewsList type={finalType} subtype={finalSubtype} />
    </div>
  );
};

export default News;
